"use client";

import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useExclusiveSearchFilterTrigger } from "@/hooks/useExclusiveSearchFilterTrigger";
import { useLocationSelector } from "@/hooks/useLocationSelector";
import { cn } from "@/lib/utils";
import type { CustomLocation, MapAreaId } from "@/utils/location";
import { Loader2Icon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { LocationSelectorContent } from "./shared/LocationSelectorContent";
import { LocationSelectorTriggerContent } from "./shared/LocationSelectorTriggerContent";

export type LocationSelectorDesktopProps = {
  className?: string;
  currentArea: MapAreaId;
  currentCustomLocation?: CustomLocation | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export default function LocationSelectorDesktop({
  className,
  currentArea,
  currentCustomLocation,
  isOpen,
  onOpenChange,
}: LocationSelectorDesktopProps) {
  const t = useTranslations();
  const {
    activeTrigger,
    setTriggerOpen,
    initialTrigger,
    setInitialTrigger,
    resetAllTriggers,
    isAnyTriggerOpen,
  } = useSearchTriggerContext();

  const closeSelector = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const {
    currentCountryOptions,
    effectiveCurrentArea,
    effectiveLabel,
    effectiveCustomLocation,
    handleAreaSelect,
    handlePlaceSelect,
    handleUseMyLocation,
    handleClose,
    searchQuery,
    setSearchQuery,
    placeSuggestions,
    isSearching,
    isDebouncing,
    isLoadingDetails,
    isReverseGeocoding,
    isGeoLoading,
    isGeoSupported,
    geoError,
  } = useLocationSelector({
    currentArea,
    currentCustomLocation,
    onClose: closeSelector,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const { openTrigger } = useExclusiveSearchFilterTrigger({
    triggerId: "location-selector",
    triggerKind: "location",
    isOpen,
    activeTrigger,
    initialTrigger,
    setTriggerOpen,
    setInitialTrigger,
    onSyncOpen: () => setSearchQuery(""),
    onDeactivateWhileOpen: handleClose,
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const openSelector = useCallback(() => {
    openTrigger();
    onOpenChange(true);
  }, [onOpenChange, openTrigger]);

  const handlePopoverOpenChange = (newIsOpen: boolean) => {
    if (newIsOpen) {
      openSelector();
    } else {
      handleClose();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (isOpen) {
        setTriggerOpen("location-selector", true);
      }
    }, 0);
  };

  const heightClass = isAnyTriggerOpen ? "h-12" : "h-10";

  return (
    <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className={cn("w-full", className)}
          data-search-bar-component="location-anchor"
        >
          {isOpen ? (
            <div
              className={cn(
                "flex items-center gap-2 px-4 relative w-full bg-white rounded-full",
                "animate-in fade-in-0 zoom-in-95 duration-200",
                heightClass,
              )}
            >
              <LocationSelectorTriggerContent
                effectiveCurrentArea={effectiveCurrentArea}
                effectiveCustomLocation={effectiveCustomLocation ?? null}
                priority
              />
              <Input
                ref={inputRef}
                placeholder={t("locationSelector.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={handleInputBlur}
                tabIndex={0}
                className="flex-1 min-w-0 h-auto p-0 border-0 shadow-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground placeholder:font-normal text-base font-medium leading-6 text-foreground rounded-none"
              />
              {(isSearching || isLoadingDetails || isReverseGeocoding) && (
                <Loader2Icon className="h-5 w-5 text-foreground animate-spin pointer-events-none shrink-0" />
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openSelector();
              }}
              className={cn(
                "flex items-center gap-2 px-4 relative w-full bg-transparent cursor-pointer",
                heightClass,
              )}
            >
              <LocationSelectorTriggerContent
                effectiveCurrentArea={effectiveCurrentArea}
                effectiveCustomLocation={effectiveCustomLocation ?? null}
                label={effectiveLabel}
                labelClassName="flex-1 min-w-0 text-left text-base font-medium leading-6 text-foreground truncate"
                priority
              />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        sideOffset={15}
        align="start"
        className="w-[360px] p-0 max-h-[70vh] overflow-hidden flex flex-col"
        onInteractOutside={(e) => {
          const target = e.target as Node | null;
          if (
            anchorRef.current &&
            target &&
            anchorRef.current.contains(target)
          ) {
            e.preventDefault();
            return;
          }
          if (target) {
            const activityAnchor = (target as HTMLElement).closest?.(
              '[data-search-bar-component="activity-anchor"]',
            );
            if (activityAnchor) {
              e.preventDefault();
              return;
            }
          }
        }}
        onEscapeKeyDown={() => resetAllTriggers()}
      >
        <div className="overflow-y-auto flex-1 py-2">
          <LocationSelectorContent
            searchQuery={searchQuery}
            placeSuggestions={placeSuggestions}
            isSearching={isSearching}
            isDebouncing={isDebouncing}
            effectiveCurrentArea={effectiveCurrentArea}
            effectiveCustomLocation={effectiveCustomLocation ?? null}
            currentCountryOptions={currentCountryOptions}
            isGeoSupported={isGeoSupported}
            isGeoLoading={isGeoLoading}
            isReverseGeocoding={isReverseGeocoding}
            geoError={geoError}
            onAreaSelect={handleAreaSelect}
            onPlaceSelect={handlePlaceSelect}
            onUseMyLocation={handleUseMyLocation}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
